import { fetchSourceResponse } from '@/yomiri/core/SourceHttp.ts';
import type { YomiriChapter, YomiriManga, YomiriPage } from '@/yomiri/types/extension.ts';
import JSZip from 'jszip';
import { yomiriDatabase } from '@/yomiri/storage/YomiriDatabase.ts';
import { cachedPageBlob } from '../core/PageBlobCache.ts';
import { isRemoteReceiver,remoteCall } from '../cast/RemoteSession.ts';

export interface LocalChapter { key: string; mangaKey: string; generation: string; manga: YomiriManga; chapter: YomiriChapter; count: number; bytes: number; savedAt: number; cover?: Blob }
let database: Promise<IDBDatabase> | undefined;
function db() {
    if (!database) database = new Promise((resolve, reject) => {
        const request = indexedDB.open('yomiri_downloads', 2);
        request.onupgradeneeded = () => {
            const connection = request.result;
            const chapters = connection.objectStoreNames.contains('chapters') ? request.transaction!.objectStore('chapters') : connection.createObjectStore('chapters', { keyPath: 'key' });
            if (!chapters.indexNames.contains('mangaKey')) chapters.createIndex('mangaKey', 'mangaKey');
            if (!connection.objectStoreNames.contains('pages')) connection.createObjectStore('pages');
            const cursor = chapters.openCursor(); cursor.onsuccess = () => { const item = cursor.result; if (item) { const row = item.value; item.update({ ...row, mangaKey: JSON.stringify([row.manga.sourceId, row.manga.id]) }); item.continue(); } };
        };
        request.onsuccess = () => {
            const connection = request.result;
            // Recover incomplete downloads left by closing the browser mid-download.
            const tx = connection.transaction(['chapters', 'pages'], 'readwrite');
            const records = tx.objectStore('chapters').getAll(); const keys = tx.objectStore('pages').getAllKeys();
            let retained: Set<string> | undefined, pageKeys: IDBValidKey[] | undefined;
            const clean = () => { if (retained && pageKeys) for (const key of pageKeys) if (typeof key === 'string' && !retained.has(key.split(':')[0])) tx.objectStore('pages').delete(key); };
            records.onsuccess = () => { retained = new Set(records.result.map((r: LocalChapter) => r.generation)); clean(); }; keys.onsuccess = () => { pageKeys = keys.result; clean(); };
            tx.oncomplete = () => resolve(connection); tx.onabort = () => { database = undefined; reject(tx.error); }; tx.onerror = () => { database = undefined; reject(tx.error); };
        };
        request.onerror = () => { database = undefined; reject(request.error); };
    });
    return database;
}
async function read<T>(store: string, key?: string): Promise<T> {
    const connection = await db(); return new Promise((resolve, reject) => {
        const request = key === undefined ? connection.transaction(store).objectStore(store).getAll() : connection.transaction(store).objectStore(store).get(key);
        request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
    });
}
async function write(store: string, action: (store: IDBObjectStore) => void) {
    const connection = await db(); return new Promise<void>((resolve, reject) => {
        const tx = connection.transaction(store, 'readwrite'); action(tx.objectStore(store)); tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error || new Error('No se pudo guardar la descarga')); tx.onerror = () => reject(tx.error);
    });
}
const chapterKey = (source: string, id: string) => JSON.stringify([source, id]);
export const listLocalChapters = () => read<LocalChapter[]>('chapters');
export async function localMangaChapters(source: string, manga: string): Promise<LocalChapter[]> {
    const connection = await db(); return new Promise((resolve, reject) => { const request = connection.transaction('chapters').objectStore('chapters').index('mangaKey').getAll(JSON.stringify([source, manga])); request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
}
export const getLocalChapter = (source: string, id: string) => read<LocalChapter | undefined>('chapters', chapterKey(source, id));
export async function localChapterPages(source: string, id: string): Promise<YomiriPage[] | undefined> {
    const chapter = await getLocalChapter(source, id);
    return chapter && Array.from({ length: chapter.count }, (_, index) => ({ index, imageUrl: `yomiri-local:${encodeURIComponent(chapter.generation)}/${index}` }));
}
export async function fetchPageBlob(page: YomiriPage, signal?: AbortSignal): Promise<Blob> {
    if(isRemoteReceiver()){
        signal?.throwIfAborted();
        const result=await remoteCall<{base64:string;mime:string}>('media.page',{page});
        signal?.throwIfAborted();
        return new Blob([Uint8Array.from(atob(result.base64),char=>char.charCodeAt(0))],{type:result.mime});
    }
    return cachedPageBlob(page, async sharedSignal => {
        try{return await fetchPageBlobUncached(page,sharedSignal);}
        catch(error){if(sharedSignal.aborted||!page.fallbackImageUrl)throw error;return fetchPageBlobUncached({...page,imageUrl:page.fallbackImageUrl,fallbackImageUrl:undefined},sharedSignal);}
    }, signal);
}
async function fetchPageBlobUncached(page: YomiriPage, signal?: AbortSignal): Promise<Blob> {
    if (page.imageUrl.startsWith('yomiri-local:')) {
        const match = page.imageUrl.match(/^yomiri-local:(.+)\/(\d+)$/);
        const blob = match && await read<Blob | undefined>('pages', `${decodeURIComponent(match[1])}:${match[2]}`);
        if (!blob) throw new Error('La página descargada no está disponible'); return blob;
    }
    let blob: Blob;
    if (page.imageUrl.startsWith('data:') || page.imageUrl.startsWith('blob:')) {
        // Local resources do not consult an external source or consume Worker quota.
        blob = await (await fetch(page.imageUrl, { signal })).blob();
    } else {
        const response = await fetchSourceResponse(page.imageUrl, { sourceId: page.sourceId, headers: page.headers, purpose: 'image', cacheTtlMs: 0, credentials: 'include', corsOpen:page.sourceId==='mangadex'?false:undefined, signal });
        if (!response.ok) throw new Error(`Imagen: HTTP ${response.status}`);
        blob = await response.blob();
    }
    if (page.encryptionKey) {
        if (!/^(?:[a-f0-9]{2})+$/i.test(page.encryptionKey)) throw new Error('Clave de imagen inválida');
        const key = page.encryptionKey.match(/../g)!.map(v => parseInt(v, 16)); const bytes = new Uint8Array(await blob.arrayBuffer());
        for (let i = 0; i < bytes.length; i++) bytes[i] ^= key[i % key.length];
        blob = new Blob([bytes], { type: 'image/jpeg' });
    }
    if (!blob.size || (blob.type && !blob.type.startsWith('image/'))) throw new Error('La fuente devolvió un archivo que no es una imagen');
    return blob;
}
export async function deleteLocalChapter(record: LocalChapter) {
    const connection = await db(); await new Promise<void>((resolve, reject) => {
        const tx = connection.transaction(['chapters', 'pages'], 'readwrite'); tx.objectStore('chapters').delete(record.key);
        for (let i = 0; i < record.count; i++) tx.objectStore('pages').delete(`${record.generation}:${i}`);
        tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error); tx.onerror = () => reject(tx.error);
    }); window.dispatchEvent(new Event('yomiri-downloads-changed'));
}
let downloading = false;
export const isLocalDownloadActive = () => downloading;
export async function downloadLocalChapter(manga: YomiriManga, chapter: YomiriChapter, pages: YomiriPage[], signal: AbortSignal, progress: (done: number, total: number) => void) {
    if (downloading) throw new Error('Esperá a que termine la descarga actual');
    if (!pages.length) throw new Error('Este capítulo no tiene páginas disponibles');
    downloading = true; const generation = crypto.randomUUID(); let written = 0, bytes = 0;
    const storedPages = new Set<number>();
    const transfers = new AbortController();
    const cancel = () => transfers.abort(signal.reason);
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) cancel();
    try {
        if (await getLocalChapter(chapter.sourceId, chapter.id)) return;
        void navigator.storage?.persist?.().catch(() => false);
        progress(0, pages.length);
        let nextPage = 0;
        let failure: unknown;
        const worker = async () => {
            try {
                while (nextPage < pages.length) {
                    transfers.signal.throwIfAborted();
                    const index = nextPage++;
                    const page = pages[index];
                    const blob = await fetchPageBlob({ ...page, sourceId: page.sourceId ?? manga.sourceId }, transfers.signal);
                    transfers.signal.throwIfAborted();
                    const image = await createImageBitmap(blob); image.close();
                    transfers.signal.throwIfAborted();
                    await write('pages', store => store.put(blob, `${generation}:${index}`));
                    storedPages.add(index); written++; bytes += blob.size; progress(written, pages.length);
                }
            } catch (error) { failure ??= error; transfers.abort(error); }
        };
        // Wait for every worker before cleanup, including writes already in progress.
        await Promise.all(Array.from({ length: Math.min(3, pages.length) }, worker));
        if (failure) throw failure;
        signal.throwIfAborted();
        let cover: Blob | undefined;
        if (manga.coverUrl) { try { cover = await fetchPageBlob({ index: 0, imageUrl: manga.coverUrl, sourceId: manga.sourceId }, signal); } catch { /* The chapter remains usable without a cover. */ } }
        signal.throwIfAborted();
        const record: LocalChapter = { key: chapterKey(chapter.sourceId, chapter.id), mangaKey: JSON.stringify([manga.sourceId, manga.id]), generation, manga, chapter, count: written, bytes: bytes + (cover?.size || 0), cover, savedAt: Date.now() };
        await write('chapters', store => store.put(record)); window.dispatchEvent(new Event('yomiri-downloads-changed'));

        // Save automatically to user-selected folder or Downloads as .cbz
        try {
            if (typeof document === 'undefined') return;
            const cbz = await exportChapterToCbz(record);
            await saveCbzToDisk(manga.title, chapter.name, cbz);
        } catch (diskError) {
            console.warn('[Yomiri] No se pudo guardar copia .cbz en disco:', diskError);
        }
    } catch (error) {
        await write('pages', store => { for (const index of storedPages) store.delete(`${generation}:${index}`); });
        if ((error as Error).name === 'QuotaExceededError') throw new Error('No queda espacio en este navegador. Borrá descargas y volvé a intentar.');
        throw error;
    } finally { signal.removeEventListener('abort', cancel); downloading = false; }
}

export function cleanFilename(name: string): string {
    return name.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').trim() || 'capitulo';
}

export async function exportChapterToCbz(record: LocalChapter): Promise<Blob> {
    const zip = new JSZip();
    for (let i = 0; i < record.count; i++) {
        const blob = await read<Blob | undefined>('pages', `${record.generation}:${i}`);
        if (blob) {
            const pageNum = String(i + 1).padStart(3, '0');
            const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
            zip.file(`${pageNum}.${ext}`, blob);
        }
    }
    return await zip.generateAsync({ type: 'blob' });
}

export async function saveCbzToDisk(
    mangaTitle: string,
    chapterName: string,
    cbzBlob: Blob,
): Promise<{ savedTo: string; destination: 'folder' | 'downloads' }> {
    try {
        const handle = await yomiriDatabase.getSetting<any>('custom_directory_handle');
        if (handle && typeof handle.getFileHandle === 'function') {
            let granted = false;
            try {
                const permission = await handle.queryPermission({ mode: 'readwrite' });
                granted = permission === 'granted' || (await handle.requestPermission({ mode: 'readwrite' })) === 'granted';
            } catch {
                granted = false;
            }
            if (granted) {
                const safeMangaName = cleanFilename(mangaTitle);
                const safeChapterName = cleanFilename(chapterName);
                const mangaRoot = await handle.getDirectoryHandle('Manga', { create: true });
                const mangaDir = await mangaRoot.getDirectoryHandle(safeMangaName, { create: true });
                const fileHandle = await mangaDir.getFileHandle(`${safeChapterName}.cbz`, { create: true });
                const writable = await fileHandle.createWritable();
                await writable.write(cbzBlob);
                await writable.close();
                return { savedTo: `carpeta "${handle.name}/Manga/${safeMangaName}/${safeChapterName}.cbz"`, destination: 'folder' };
            }
        }
    } catch (err) {
        console.warn('[Yomiri] Error guardando en carpeta personalizada, guardando en Descargas:', err);
    }

    // Fallback por defecto: Carpeta Descargas del sistema a través del navegador
    const safeFilename = `${cleanFilename(mangaTitle)} - ${cleanFilename(chapterName)}.cbz`;
    const url = URL.createObjectURL(cbzBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 3000);
    return { savedTo: `Descargas (${safeFilename})`, destination: 'downloads' };
}

